import { chavePagina, combinarConfig, paginaNoSubsistema, SUBSISTEMAS_ATIVOS, subsistemaLigado, type ConfigSubsistemas } from '@malgoos/shared';

/**
 * D1 (sub-sistemas, L3): edição da arrumação na tela Admin > Configurações > Sub-sistemas (funções puras, testadas em
 * `tests/subsistemas-d1-config.test.ts`). O escopo é o workspace ou uma empresa; na empresa, a tela mostra o efeito (o
 * workspace com a empresa por cima) e grava na empresa só o que ela muda. O servidor compacta (só diferenças do padrão).
 */

export interface Escopo {
  /** null: o workspace. */
  empresaId: string | null;
  workspace: ConfigSubsistemas;
  /** A da empresa (null: segue o workspace). */
  empresa: ConfigSubsistemas | null;
}

/** O que vale na tela para o escopo. */
export const efetivaDoEscopo = (e: Escopo): ConfigSubsistemas => (e.empresaId ? combinarConfig(e.workspace, e.empresa ?? {}) : e.workspace);

/** A configuração que o escopo grava (a do workspace ou a da empresa). */
export const doEscopo = (e: Escopo): ConfigSubsistemas => (e.empresaId ? (e.empresa ?? {}) : e.workspace);

const comEscopo = (e: Escopo, c: ConfigSubsistemas): Escopo => (e.empresaId ? { ...e, empresa: c } : { ...e, workspace: c });

export function alternarPagina(e: Escopo, sub: string, slug: string, aba: string, ligar: boolean): Escopo {
  const c = doEscopo(e);
  const chave = chavePagina(slug, aba);
  return comEscopo(e, { ...c, paginas: { ...(c.paginas ?? {}), [chave]: { ...(c.paginas?.[chave] ?? {}), [sub]: ligar } } });
}

/** Desliga o módulo no sub-sistema (todas as páginas dele ali) ou religa (volta ao que as páginas estavam). */
export function alternarModulo(e: Escopo, sub: string, slug: string, ligar: boolean): Escopo {
  const atual = efetivaDoEscopo(e).modulos?.[sub] ?? [];
  const lista = ligar ? atual.filter((s) => s !== slug) : [...new Set([...atual, slug])].sort();
  const c = doEscopo(e);
  return comEscopo(e, { ...c, modulos: { ...(c.modulos ?? {}), [sub]: lista } });
}

export function alternarSubsistema(e: Escopo, sub: string, ligar: boolean): Escopo {
  const atual = efetivaDoEscopo(e).desligados ?? [];
  const desligados = ligar ? atual.filter((s) => s !== sub) : [...new Set([...atual, sub])].sort();
  return comEscopo(e, { ...doEscopo(e), desligados });
}

/** "Restaurar padrão" de um sub-sistema no escopo: tira as escolhas dele (no workspace volta ao padrão; na empresa, ao workspace). */
export function restaurarSubsistema(e: Escopo, sub: string): Escopo {
  const c = doEscopo(e);
  const paginas: Record<string, Record<string, boolean>> = {};
  for (const [k, v] of Object.entries(c.paginas ?? {})) {
    const { [sub]: _fora, ...resto } = v;
    if (Object.keys(resto).length) paginas[k] = resto;
  }
  const { [sub]: _mod, ...modulos } = c.modulos ?? {};
  const base = e.empresaId ? (e.workspace.desligados ?? []).includes(sub) : false;
  const desligados = c.desligados ? [...c.desligados.filter((s) => s !== sub), ...(base ? [sub] : [])].sort() : undefined;
  return comEscopo(e, { ...c, paginas, modulos, ...(desligados ? { desligados } : {}) });
}

/** "Restaurar padrão" geral: no workspace, só o que é arrumação (mantém ligado, novo desenho, ícones e links); na empresa, segue o workspace. */
export function restaurarTudo(e: Escopo): Escopo {
  if (e.empresaId) return { ...e, empresa: {} };
  const { ativo, novoDesenho, icones, links } = e.workspace;
  return { ...e, workspace: { ...(ativo !== undefined ? { ativo } : {}), ...(novoDesenho !== undefined ? { novoDesenho } : {}), ...(icones ? { icones } : {}), ...(links ? { links } : {}) } };
}

export type EstadoModulo = 'desligado' | 'ligado' | 'parcial' | 'nenhuma';

/** Linha do módulo: desligado no sub-sistema, todas as páginas, parte delas ou nenhuma. */
export function estadoDoModulo(c: ConfigSubsistemas, sub: string, slug: string, abas: readonly string[]): { estado: EstadoModulo; ligadas: number } {
  if (c.modulos?.[sub]?.includes(slug)) return { estado: 'desligado', ligadas: 0 };
  const ligadas = abas.filter((a) => paginaNoSubsistema(c, sub, slug, a)).length;
  return { estado: ligadas === 0 ? 'nenhuma' : ligadas === abas.length ? 'ligado' : 'parcial', ligadas };
}

/** Sub-sistemas da tela (todos os em uso) com o estado ligado no escopo. */
export const subsistemasDoEscopo = (e: Escopo) => SUBSISTEMAS_ATIVOS.map((sub) => ({ sub, ligado: subsistemaLigado(efetivaDoEscopo(e), sub) }));
